import { useState } from 'react';
import { Badge, Button, Group, Select, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import {
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  invoicesApi,
  type Invoice,
  type InvoiceStatus,
} from '../api/invoices';
import { useAuth } from '../auth/AuthContext';
import { RegistryPage } from '../components/RegistryPage';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  ...Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU');
}

export function InvoicesPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [status, setStatus] = useState<InvoiceStatus | ''>('');

  const list = useQuery({
    queryKey: ['invoices', { search: debouncedSearch, status }],
    queryFn: () => invoicesApi.list({ search: debouncedSearch, status: status || undefined }),
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Счета на оплату"
      action={
        canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/invoices/new')}>
            Создать счёт
          </Button>
        )
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по номеру или контрагенту"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
          <Select
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setStatus((v as InvoiceStatus) || '')}
            w={220}
            allowDeselect={false}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Номер</Table.Th>
            <Table.Th>Дата</Table.Th>
            <Table.Th>Организация</Table.Th>
            <Table.Th>Контрагент</Table.Th>
            <Table.Th>Сумма</Table.Th>
            <Table.Th>Статус</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((invoice: Invoice) => (
            <Table.Tr
              key={invoice.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/invoices/${invoice.id}`)}
            >
              <Table.Td>
                <Text size="sm" fw={500}>
                  № {invoice.number}
                </Text>
              </Table.Td>
              <Table.Td>{formatDate(invoice.date)}</Table.Td>
              <Table.Td>{invoice.organization.shortName}</Table.Td>
              <Table.Td>{invoice.counterparty.shortName}</Table.Td>
              <Table.Td>{formatMoney(invoice.total)}</Table.Td>
              <Table.Td>
                <Badge color={INVOICE_STATUS_COLORS[invoice.status]} variant="light">
                  {INVOICE_STATUS_LABELS[invoice.status]}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Счета не найдены
        </Text>
      )}
    </RegistryPage>
  );
}
